import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Linkedin, Send, CheckCircle } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { toast, Toaster } from 'sonner';
import WhatsAppFloatingButton from "../pages/WhatsAppFloatingButton";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 12 },
    },
  };

  const services = [
    'Content Marketing',
    'Social Media Marketing',
    'Brand Building',
    'Thought Leadership',
    'Digital Presence',
    'Other',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  try {
    const response = await fetch(
      "https://mowgicommunications.com/wordpress/wp-json/custom/v1/contact",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          service_interested: formData.service, // IMPORTANT MAPPING
          message: formData.message,
        }),
      }
    );

    const result = await response.json();

    if (!response.ok || result.success === false) {
      throw new Error(result.message || "Something went wrong");
    }

    setIsSubmitted(true);
    toast.success("Thank you! We'll be in touch soon.");

    // Reset form
    setFormData({
      name: "",
      email: "",
      company: "",
      service: "",
      message: "",
    });

  } catch (error) {
    toast.error(error.message || "Failed to submit form");
  } finally {
    setIsSubmitting(false);
  }
};


  return (
    <div className="min-h-screen pt-20">
      <Toaster position="top-center" richColors />
<WhatsAppFloatingButton />
      {/* Hero Section */}
      <section className="py-24 lg:py-32 bg-royal-blue relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1600&q=80" 
            alt="Meeting"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="max-w-3xl"
          >
            <motion.span variants={itemVariants} className="service-tag bg-light-blue text-royal-blue">
              Contact Us
            </motion.span>
            <motion.h1 variants={itemVariants} className="text-5xl md:text-6xl font-bold mt-6 mb-8 text-white">
              Let's <span className="text-blue-300">Connect</span>
            </motion.h1>
            <motion.p variants={itemVariants} className="text-xl text-blue-100 leading-relaxed">
              Ready to elevate your brand? Fill out the form below and we'll get back to you within 24 hours.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={containerVariants}
            className="grid lg:grid-cols-2 gap-16"
          >
            {/* Form */}
            <motion.div variants={itemVariants}>
              <h2 className="text-3xl font-bold mb-8 text-navy">Send Us a Message</h2>

              {isSubmitted ? (
                <div className="bg-sage-light rounded-lg p-8 text-center">
                  <CheckCircle className="w-16 h-16 text-sage mx-auto mb-4" />
                  <h3 className="text-2xl font-bold mb-2 text-navy">Message Sent!</h3>
                  <p className="text-steel-blue">Thank you for reaching out. We'll be in touch soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-slate-blue mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-md border border-gray-200 focus:outline-none focus:border-royal-blue focus:ring-2 focus:ring-royal-blue/20 transition-all"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-blue mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-md border border-gray-200 focus:outline-none focus:border-royal-blue focus:ring-2 focus:ring-royal-blue/20 transition-all"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-slate-blue mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 focus:outline-none focus:border-royal-blue focus:ring-2 focus:ring-royal-blue/20 transition-all"
                      placeholder="Your Company"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-semibold text-slate-blue mb-2">
                      Service Interested In
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 focus:outline-none focus:border-royal-blue focus:ring-2 focus:ring-royal-blue/20 transition-all appearance-none bg-white"
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-blue mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 focus:outline-none focus:border-royal-blue focus:ring-2 focus:ring-royal-blue/20 transition-all resize-none"
                      placeholder="Tell us about your project or how we can help..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="cta-button w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <Send className="w-4 h-4 mr-2" />
                        Let's Build Influence
                      </span>
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div variants={itemVariants} className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-8 text-navy">Get in Touch</h2>
                <p className="text-steel-blue leading-relaxed">
                  We're here to help you navigate the marketing landscape. Whether you're looking to build your brand, create compelling content, or establish thought leadership, we'd love to hear from you.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                <div className="bg-light-blue p-6 rounded-lg card-hover">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-royal-blue flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-steel-blue">Email Us</p>
                      <a
                        href={`mailto:${companyInfo.email}`}
                        className="font-bold text-navy hover:text-royal-blue transition-colors"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-ice-blue p-6 rounded-lg card-hover">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-royal-blue flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-steel-blue">Location</p>
                      <p className="font-bold text-navy">{companyInfo.address}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-sage-light p-6 rounded-lg card-hover">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-lg bg-sage flex items-center justify-center">
                      <Linkedin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-steel-blue">Connect</p>
                      <a
                        href={companyInfo.linkedIn}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold text-navy hover:text-royal-blue transition-colors"
                      >
                        LinkedIn
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Why Contact Us */}
              <div className="bg-snow p-8 rounded-lg border border-gray-100">
                <h3 className="font-bold text-lg mb-4 text-navy">What Happens Next?</h3>
                <ul className="space-y-4">
                  {[
                    'We\'ll review your message within 24 hours',
                    'Schedule a discovery call to understand your needs',
                    'Present a tailored proposal for your consideration',
                    'Begin our partnership to build influence',
                  ].map((step, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-6 h-6 rounded-md bg-royal-blue flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-white">{index + 1}</span>
                      </div>
                      <span className="text-steel-blue">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
